/**
 * UX-033 (CR-W3-05-1): the "Made a jump at {beat}" toast of one-sided keyframe edits — a copy of the note lane's
 * `reportJumpsMade` (pasteFlow.ts; the zones keep features from importing each other). The inspector's From / To
 * fields write one side of a key: on a continuous key `setKeySide` keeps it continuous (the whole key moves), but
 * after a Stay the edit must make a jump, and the toast says so with Undo and "Continue from previous".
 *
 * Record `continuityBefore(store.doc, keys)` BEFORE the transaction (the store's document changes in place), then
 * `reportJumpsMade` after it.
 */

import type { AppServices } from '../../app/registry/types';
import { continuityOf, jumpsMade } from '../../core/commands/keyValueRules';
import type { ChartDoc, KeyId } from '../../core/model/types';
import type { Beat } from '../../core/time/types';

/** Continuity of each key (true = continuous) before the edit. */
export function continuityBefore(doc: ChartDoc, keys: readonly KeyId[]): Map<KeyId, boolean> {
  return continuityOf(doc, keys);
}

/** Toast when `before`-continuous keys became jumps (no-op when none did). */
export function reportJumpsMade(
  services: AppServices,
  fmtBeat: (b: Beat) => string,
  before: ReadonlyMap<KeyId, boolean>,
  keys: readonly KeyId[],
): void {
  const store = services.store;
  if (store === null || keys.length === 0) return;
  const made = jumpsMade(before, { doc: store.doc, idx: store.idx }, keys);
  const first = made[0] === undefined ? undefined : store.doc.keys.get(made[0]);
  if (first === undefined) return;
  services.ui.toast(
    { key: 'noteLane.toastJumpMade', params: { count: made.length, at: fmtBeat(first.t) } },
    {
      kind: 'info',
      undo: true,
      durationMs: 8000,
      action: {
        label: { key: 'noteLane.actionContinueFromPrevious' },
        command: 'segments.jump',
        args: { keys: made, keep: 'in' },
      },
    },
  );
}
